"use client";

import React from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  Box,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { agentService } from "../../services/agentService.ts";
import "flag-icons/css/flag-icons.css";
import { useAppTheme } from "../../ThemeContext.tsx";

interface LanguageSelectProps {
  initialLanguage: string;
  agentId: string;
}

const languageOptions = [
  { code: "en-US", name: "English (US)", flag: "us" },
  { code: "en-IN", name: "English (India)", flag: "in" },
  { code: "en-GB", name: "English (UK)", flag: "gb" },
  { code: "de-DE", name: "German", flag: "de" },
  { code: "es-ES", name: "Spanish (Spain)", flag: "es" },
  { code: "es-419", name: "Spanish (Latin America)", flag: "es" },
  { code: "hi-IN", name: "Hindi", flag: "in" },
  { code: "ja-JP", name: "Japanese", flag: "jp" },
  { code: "pt-PT", name: "Portuguese (Portugal)", flag: "pt" },
  { code: "pt-BR", name: "Portuguese (Brazil)", flag: "br" },
  { code: "fr-FR", name: "French", flag: "fr" },
  { code: "zh-CN", name: "Chinese (Simplified)", flag: "cn" },
  { code: "ru-RU", name: "Russian", flag: "ru" },
  { code: "it-IT", name: "Italian", flag: "it" },
  { code: "ko-KR", name: "Korean", flag: "kr" },
  { code: "nl-NL", name: "Dutch", flag: "nl" },
  { code: "pl-PL", name: "Polish", flag: "pl" },
  { code: "tr-TR", name: "Turkish", flag: "tr" },
  { code: "vi-VN", name: "Vietnamese", flag: "vn" },
  { code: "multi", name: "Multilingual", flag: "un" },
];

export const LanguageSelect: React.FC<LanguageSelectProps> = React.memo(
  ({ initialLanguage, agentId }) => {
    const [selectedLanguage, setSelectedLanguage] =
      React.useState<string>(initialLanguage);
    const toast = useToast();

    React.useEffect(() => {
      setSelectedLanguage(initialLanguage);
    }, [initialLanguage]);
    const { resolvedTheme } = useAppTheme();
    const isDark = resolvedTheme === "dark";

    const handleLanguageChange = React.useCallback(
      async (languageCode: string) => {
        try {
          await agentService.update(agentId, {
            language: languageCode,
          });

          setSelectedLanguage(languageCode);
          toast({
            title: "Language updated successfully",
            status: "success",
            duration: 2000,
          });
        } catch (error: any) {
          toast({
            title: "Error updating language",
            description:
              error.response?.data?.message || "An unexpected error occurred",
            status: "error",
            duration: 3000,
          });
        }
      },
      [agentId, toast]
    );

    const selectedLang =
      languageOptions.find((lang) => lang.code === selectedLanguage) ||
      languageOptions[0];

    return (
      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          borderColor={isDark ? "gray.600" : "gray.300"}
          color={isDark ? "white" : "gray.800"}
          rightIcon={<ChevronDownIcon />}
        >
          <HStack spacing={2}>
            <Box
              as="span"
              className={`fi fi-${selectedLang.flag}`}
              width="1.5em"
              height="1.5em"
              mr={2}
            />
            <Text>{selectedLang.name}</Text>
          </HStack>
        </MenuButton>
        <MenuList
          bg={isDark ? "gray.800" : "white"}
          borderColor={isDark ? "gray.600" : "gray.300"}
          maxH="300px"
          overflowY="auto"
          // --- Add custom scrollbar styling here:
          sx={{
            // Chrome, Safari, Edge
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: isDark ? "gray.700" : "gray.100",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "gray.600" : "gray.300",
              borderRadius: "6px",
            },
            // Firefox
            scrollbarWidth: "thin",
            scrollbarColor: isDark
              ? "#4A5568 #2D3748" /* thumb #4A5568, track #2D3748 */
              : "#CBD5E0 #EDF2F7" /* thumb #CBD5E0, track #EDF2F7 */,
          }}
        >
          {languageOptions.map((lang) => {
            const isSelected = lang.code === selectedLanguage;
            return (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              bg={
                isSelected
                  ? isDark
                    ? "gray.700"
                    : "gray.100"
                  : "transparent"
              }
              fontWeight={isSelected ? "semibold" : "normal"}
              _hover={{ bg: isDark ? "gray.600" : "gray.50" }}
              color={isDark ? "white" : "gray.800"}
            >
              <HStack spacing={2}>
                <Box
                  as="span"
                  className={`fi fi-${lang.flag}`}
                  width="1.5em"
                  height="1.5em"
                  mr={2}
                />
                <Text>{lang.name}</Text>
              </HStack>
            </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    );
  }
);

LanguageSelect.displayName = "LanguageSelect";
